// ShareBite Donation field validation (Task 2.5).
//
// Pure, platform-independent validator for a Donation creation payload. The
// function decides whether a submission is acceptable and, when it is not,
// reports every violated constraint while retaining the entered values so the
// client can re-render the form without data loss. It performs no I/O and
// produces no side effects; the caller (a Cloud Function) is responsible for
// actually creating the Donation record only when validation succeeds.
//
// Design reference: design.md "Property 6: Donation field validation".
// Requirements:
//   8.2  - 1 to 5 Images required.
//   8.3  - Meal Count is a whole number in 1..9,999.
//   8.4  - Pickup Deadline strictly later than the Preparation Time.
//   13.1 - Pickup Deadline between 30 minutes and 3 hours after creation time.
//   13.2 - Pickup Deadline outside that window is rejected (entered data kept).
//   24.1 - Meal Count is a positive whole number.
//   24.2 - Food Name 1..100, Description 0..1000, Special Instructions 0..500.
/**
 * The minimum gap (in epoch milliseconds) the Pickup Deadline must sit after
 * the Donation creation time: 30 minutes. The boundary is inclusive — a
 * deadline exactly 30 minutes after creation is valid (Req 13.1).
 */
export const MIN_PICKUP_OFFSET_MS = 30 * 60 * 1000; // 1,800,000
/**
 * The maximum gap (in epoch milliseconds) the Pickup Deadline may sit after
 * the Donation creation time: 3 hours. The boundary is inclusive — a deadline
 * exactly 3 hours after creation is valid (Req 13.1).
 */
export const MAX_PICKUP_OFFSET_MS = 3 * 60 * 60 * 1000; // 10,800,000
/** Inclusive bounds for the number of Images on a Donation (Req 8.2). */
export const MIN_IMAGES = 1;
export const MAX_IMAGES = 5;
/** Inclusive bounds for Meal Count (Req 8.3 / 24.1). */
export const MIN_MEAL_COUNT = 1;
export const MAX_MEAL_COUNT = 9999;
/** Inclusive character-length bounds for free-text fields (Req 24.2). */
export const MAX_FOOD_NAME_LENGTH = 100;
export const MIN_FOOD_NAME_LENGTH = 1;
export const MAX_DESCRIPTION_LENGTH = 1000;
export const MAX_SPECIAL_INSTRUCTIONS_LENGTH = 500;
/**
 * Validate a Donation creation payload against the field-level rules.
 *
 * A submission is accepted if and only if ALL of the following hold:
 *  - `imageRefs` length is in 1..5 (Req 8.2);
 *  - `mealCount` is a whole number (integer) in 1..9,999 (Req 8.3, 24.1);
 *  - `foodName` length is in 1..100 (Req 24.2);
 *  - `description` length is in 0..1000 (Req 24.2);
 *  - `specialInstructions` length is in 0..500 (Req 24.2);
 *  - `pickupDeadline` is strictly greater than `preparationTime` (Req 8.4); and
 *  - `pickupDeadline` lies within [createdAt + 30 min, createdAt + 3 h],
 *    inclusive of both boundaries (Req 13.1, 13.2).
 *
 * On any failure the function reports every violated constraint and returns the
 * entered payload unchanged in `retained`; it never throws and never mutates
 * its input.
 *
 * @param payload The entered Donation creation payload.
 * @returns A {@link DonationValidationResult} describing acceptance and any
 *          violations, always echoing the entered values back.
 */
export function validateDonationFields(payload) {
    const violations = [];
    // Images: 1..5 (Req 8.2).
    const imageCount = payload.imageRefs.length;
    if (imageCount < MIN_IMAGES || imageCount > MAX_IMAGES) {
        violations.push("IMAGE_COUNT_OUT_OF_RANGE");
    }
    // Meal Count: whole number 1..9,999 (Req 8.3, 24.1). A non-integer (incl.
    // NaN/Infinity) and an out-of-range integer are distinct violations.
    if (!Number.isInteger(payload.mealCount)) {
        violations.push("MEAL_COUNT_NOT_WHOLE_NUMBER");
    }
    else if (payload.mealCount < MIN_MEAL_COUNT ||
        payload.mealCount > MAX_MEAL_COUNT) {
        violations.push("MEAL_COUNT_OUT_OF_RANGE");
    }
    // Food Name: 1..100 characters (Req 24.2).
    const foodNameLength = payload.foodName.length;
    if (foodNameLength < MIN_FOOD_NAME_LENGTH ||
        foodNameLength > MAX_FOOD_NAME_LENGTH) {
        violations.push("FOOD_NAME_LENGTH_OUT_OF_RANGE");
    }
    // Description: 0..1000 characters (Req 24.2).
    if (payload.description.length > MAX_DESCRIPTION_LENGTH) {
        violations.push("DESCRIPTION_LENGTH_OUT_OF_RANGE");
    }
    // Special Instructions: 0..500 characters (Req 24.2).
    if (payload.specialInstructions.length > MAX_SPECIAL_INSTRUCTIONS_LENGTH) {
        violations.push("SPECIAL_INSTRUCTIONS_LENGTH_OUT_OF_RANGE");
    }
    // Pickup Deadline strictly after Preparation Time (Req 8.4).
    if (!(payload.pickupDeadline > payload.preparationTime)) {
        violations.push("PICKUP_DEADLINE_NOT_AFTER_PREPARATION_TIME");
    }
    // Pickup Deadline within [createdAt + 30 min, createdAt + 3 h], inclusive
    // (Req 13.1, 13.2).
    const earliest = payload.createdAt + MIN_PICKUP_OFFSET_MS;
    const latest = payload.createdAt + MAX_PICKUP_OFFSET_MS;
    if (payload.pickupDeadline < earliest || payload.pickupDeadline > latest) {
        violations.push("PICKUP_DEADLINE_OUTSIDE_ALLOWED_WINDOW");
    }
    if (violations.length === 0) {
        return { valid: true, violations: [], retained: payload };
    }
    return { valid: false, violations, retained: payload };
}
//# sourceMappingURL=donationValidation.js.map